/**
 * Iter 67fc — Admin analytics tile: widget-attributed bookings vs organic.
 *
 * Renders 3 KPIs (widget bookings, widget revenue, share % of all bookings),
 * a stacked bar chart of the last N days, and a "Top widget converters"
 * leaderboard. Powered by `/api/admin/analytics/widget-attribution`.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe2, Loader2, TrendingUp, Wallet, Percent } from "lucide-react";
import { toast } from "sonner";

function Kpi({ icon: Icon, label, value, sub, tone = "indigo", testId }) {
  const tones = {
    indigo:  "bg-indigo-50 text-indigo-700 ring-indigo-200",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    amber:   "bg-amber-50 text-amber-800 ring-amber-200",
  };
  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">{label}</div>
        <span className={`h-8 w-8 rounded-lg ring-1 flex items-center justify-center ${tones[tone] || tones.indigo}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="text-2xl font-bold mt-2">{value}</div>
      {sub && <div className="text-[11px] text-neutral-500 mt-1">{sub}</div>}
    </div>
  );
}

function _fmtCurrency(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return `€${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function _fmtDate(d) {
  if (!d) return "";
  try {
    const dt = new Date(d + "T00:00:00Z");
    return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return d; }
}

export default function AdminWidgetAttributionTile() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/admin/analytics/widget-attribution?days=${days}`)
      .then((res) => { if (active) setData(res.data); })
      .catch((e) => { if (active) toast.error(formatApiError(e)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [days]);

  const chartData = useMemo(() => {
    if (!data?.daily) return [];
    return data.daily.map((d) => ({
      ...d,
      label: _fmtDate(d.date),
    }));
  }, [data]);

  if (loading && !data) {
    return (
      <div className="rounded-2xl border bg-white p-10 flex items-center justify-center text-neutral-500" data-testid="widget-attribution-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading widget attribution…
      </div>
    );
  }
  if (!data) return null;

  const t = data.totals;
  const totalBookings = (t.widget?.count || 0) + (t.organic?.count || 0);

  return (
    <div className="space-y-4" data-testid="admin-widget-attribution-tile">
      {/* Header + range picker */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Globe2 className="h-5 w-5 text-indigo-600" />
            Widget vs organic attribution
          </h3>
          <p className="text-xs text-neutral-500">
            Bookings made via vendor micro-sites / embeds (
            <code className="px-1 rounded bg-neutral-100">source_vendor_slug</code>)
            compared with organic marketplace traffic.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Range</label>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-[120px] h-9" data-testid="widget-attribution-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 180 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi
          icon={Globe2}
          label="Widget bookings"
          value={(t.widget?.count || 0).toLocaleString()}
          sub={`of ${totalBookings.toLocaleString()} total`}
          tone="indigo"
          testId="kpi-widget-count"
        />
        <Kpi
          icon={Wallet}
          label="Widget revenue"
          value={_fmtCurrency(t.widget?.revenue)}
          sub={`${_fmtCurrency(t.widget?.paid_revenue)} paid online`}
          tone="emerald"
          testId="kpi-widget-revenue"
        />
        <Kpi
          icon={Percent}
          label="Widget share"
          value={`${t.widget_share_pct || 0}%`}
          sub={`organic ${(100 - (t.widget_share_pct || 0)).toFixed(1)}%`}
          tone="amber"
          testId="kpi-widget-share"
        />
        <Kpi
          icon={TrendingUp}
          label="Organic bookings"
          value={(t.organic?.count || 0).toLocaleString()}
          sub={_fmtCurrency(t.organic?.revenue)}
          tone="indigo"
          testId="kpi-organic-count"
        />
      </div>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Daily bookings by source</CardTitle>
          <CardDescription>Stacked bars · widget on top of organic</CardDescription>
        </CardHeader>
        <CardContent>
          <div style={{ width: "100%", height: 280 }} data-testid="widget-attribution-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  formatter={(value, name) => [value, name === "widget" ? "Widget" : "Organic"]}
                  labelFormatter={(l) => l}
                />
                <Legend
                  wrapperStyle={{ fontSize: 12 }}
                  formatter={(v) => (v === "widget" ? "Widget" : "Organic")}
                />
                <Bar dataKey="organic" stackId="bookings" fill="#94a3b8" radius={[0, 0, 0, 0]} />
                <Bar dataKey="widget"  stackId="bookings" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Top vendors */}
      {data.top_vendors?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top widget converters</CardTitle>
            <CardDescription>Vendors whose widget brought in the most bookings in this window.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto" data-testid="widget-attribution-top-vendors">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-neutral-500 border-b">
                  <tr>
                    <th className="text-left py-2 pr-2">Vendor</th>
                    <th className="text-left py-2 pr-2">Slug</th>
                    <th className="text-right py-2 pr-2">Bookings</th>
                    <th className="text-right py-2 pr-2">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_vendors.map((v) => (
                    <tr key={v.vendor_id} className="border-b last:border-b-0" data-testid={`top-vendor-row-${v.vendor_id}`}>
                      <td className="py-2 pr-2">
                        <div className="flex items-center gap-2">
                          <span
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: v.brand_color || "#6366f1" }}
                            aria-hidden
                          />
                          <span className="font-semibold">{v.business_name}</span>
                        </div>
                      </td>
                      <td className="py-2 pr-2 font-mono text-xs text-neutral-600">
                        <a href={`/v/${v.public_slug}`} target="_blank" rel="noreferrer" className="hover:underline">
                          /v/{v.public_slug}
                        </a>
                      </td>
                      <td className="py-2 pr-2 text-right tabular-nums">{v.bookings}</td>
                      <td className="py-2 pr-2 text-right tabular-nums">{_fmtCurrency(v.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
